import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

export const _frontmatter = {
  "title": "Downloads",
  "includeToc": true,
  "layout": "downloads",
  "description": "You can download data for production volumes, revenue, and disbursements here. All data is from the Office of Natural Resources Revenue.",
  "tags": ["Data", "Downloads", "Documentation", "Validation", "Collection"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const GlossaryTerm = makeShortcode("GlossaryTerm");
const Link = makeShortcode("Link");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "Downloads",
      "style": {
        "position": "relative"
      }
    }}>{`Downloads`}</h1>
    <p>{`You can download data for production volumes, revenue, and disbursements here.
All data is from the `}<a parentName="p" {...{
        "href": "https://www.onrr.gov/"
      }}>{`Office of Natural Resources Revenue`}</a>{`.`}</p>
    <h2 {...{
      "id": "Production",
      "style": {
        "position": "relative"
      }
    }}>{`Production`}</h2>
    <hr></hr>
    <h3 {...{
      "id": "Production-by-month",
      "style": {
        "position": "relative"
      }
    }}>{`Production by month`}</h3>
    <p>{`This dataset includes commodity volumes of natural resources extracted from federal lands and waters and Native American lands by month.
We offer data from January 2003 through the most recently available month.
`}<a parentName="p" {...{
        "href": "/sites/Update-hyperlinks/downloads/production-by-month/"
      }}>{`Downloads and documentation`}</a></p>
    <h3 {...{
      "id": "Production-by-year",
      "style": {
        "position": "relative"
      }
    }}>{`Production by year`}</h3>
    <p>{`This dataset includes commodity volumes of natural resources extracted from Native American lands and federal lands and waters.
Production volumes are available by state, county, and offshore area for federal production data. Location data is `}<GlossaryTerm mdxType="GlossaryTerm">{`withheld`}</GlossaryTerm>{`
for Native American data. We offer production data for calendar and fiscal years.
`}<a parentName="p" {...{
        "href": "/sites/Update-hyperlinks/downloads/production/"
      }}>{`Downloads and documentation`}</a></p>
    <h3 {...{
      "id": "Production-disposition-by-month",
      "style": {
        "position": "relative"
      }
    }}>{`Production disposition by month`}</h3>
    <p>{`This dataset contains information on natural resource production disposition volumes from ONRR’s OGOR-B form. These volumes come from production of Native American minerals and U.S. federal minerals on both land (onshore) and water (offshore).
This dataset contains monthly information on the disposition of production for each product (oil and gas) produced. The product could have been directly removed/sold, directly used (injected), moved off the lease, transferred to a different facility, flared, spilled and/or lost, lost through evaporation/shrinkage, etc.
`}<a parentName="p" {...{
        "href": "/sites/Update-hyperlinks/downloads/production-by-disposition/"
      }}>{`Downloads and documentation`}</a></p>
    <h2 {...{
      "id": "Revenue",
      "style": {
        "position": "relative"
      }
    }}>{`Revenue`}</h2>
    <hr></hr>
    <h3 {...{
      "id": "Revenue-1",
      "style": {
        "position": "relative"
      }
    }}>{`Revenue`}</h3>
    <p>{`These datasets offer revenue by month for January 2003 through the most recently completed month of the current year as well as calendar year from 2003 through the most recently completed year and fiscal year from 2004 through the most recently completed year. The data includes federal
offshore and onshore revenue, along with revenue from Native American lands. The data is further broken down by location, lease type, revenue type (i.e., bonuses, rents, and royalties)
and commodity information.
`}<a parentName="p" {...{
        "href": "/sites/Update-hyperlinks/downloads/revenue/"
      }}>{`Downloads and documentation`}</a></p>
    <h3 {...{
      "id": "Historical-revenue",
      "style": {
        "position": "relative"
      }
    }}>{`Historical revenue`}</h3>
    <p>{`This dataset offers revenue by calendar year from 1982 through 2002. The data includes federal offshore and onshore revenue.
The data is further broken down by location, revenue type (i.e., bonuses, rents, and royalties) and commodity information.`}</p>
    <br />
    <p>{`Download historical revenue data:`}</p>
    <Link linkType="DownloadCsv" href="./downloads/historical_revenuecsv_CY1982-2002.csv" mdxType="Link">
  Historical revenue, CY 1982 - 2002
    </Link>
    <Link linkType="DownloadXls" href="./downloads/historical_revenue_CY1982-2002.xlsx" mdxType="Link">
  Historical revenue, CY 1982 - 2002
    </Link>
    <h3 {...{
      "id": "Federal-revenue-by-company",
      "style": {
        "position": "relative"
      }
    }}>{`Federal revenue by company`}</h3>
    <p>{`This dataset provides natural resource federal revenue by company. It includes revenues by production phase and commodity for companies extracting natural resources on
federal lands and waters. It does not include company revenue from Native American lands or privately owned lands.
`}<a parentName="p" {...{
        "href": "/sites/Update-hyperlinks/downloads/federal-revenue-by-company/"
      }}>{`Downloads and documentation`}</a></p>
    <h2 {...{
      "id": "Disbursements",
      "style": {
        "position": "relative"
      }
    }}>{`Disbursements`}</h2>
    <hr></hr>
    <h3 {...{
      "id": "Disbursements-by-month",
      "style": {
        "position": "relative"
      }
    }}>{`Disbursements by month`}</h3>
    <p>{`The amount of money earned from extraction of natural resources on federal lands that is disbursed to various legislated funds every month.
`}<a parentName="p" {...{
        "href": "/sites/Update-hyperlinks/downloads/disbursements-by-month/"
      }}>{`Downloads and documentation`}</a></p>
    <h3 {...{
      "id": "Disbursements-by-year",
      "style": {
        "position": "relative"
      }
    }}>{`Disbursements by year`}</h3>
    <p>{`The amount of money earned from extraction of natural resources on federal lands that is disbursed to various legislated funds by fiscal year.
`}<a parentName="p" {...{
        "href": "/sites/Update-hyperlinks/downloads/disbursements/"
      }}>{`Downloads and documentation`}</a></p>
    <h3 {...{
      "id": "Historical-disbursements",
      "style": {
        "position": "relative"
      }
    }}>{`Historical disbursements`}</h3>
    <p>{`This dataset offers disbursements data by fiscal year from 1982 through 2002.`}</p>
    <br />
    <p>{`Download historical disbursements data:`}</p>
    <Link linkType="DownloadCsv" href="./downloads/historical_disbursementscsv_ FY1982-2002.csv" mdxType="Link">
  Historical disbursements, FY 1982 - 2002
    </Link>
    <Link linkType="DownloadXls" href="./downloads/historical_disbursements_FY1982-2002.xlsx" mdxType="Link">
  Historical disbursements, FY 1982 - 2002
    </Link>
    <h2 {...{
      "id": "Data-Collection-and-Validation",
      "style": {
        "position": "relative"
      }
    }}>{`Data Collection and Validation`}</h2>
    <hr></hr>
    <p>{`As a steward of natural resources and energy revenue, the Office of Natural Resources Revenue (ONRR) is responsible for three main functions. These functions are: collecting the revenue, verifying the revenue, and disbursing the revenue. ONRR collects and manages data in order to accomplish its functions. This data is the foundation for the production, revenue, and disbursement datasets found on this site.`}</p>
    <h3 {...{
      "id": "Process",
      "style": {
        "position": "relative"
      }
    }}>{`Process`}</h3>
    <p>{`ONRR's data management processes include collection and validation.`}</p>
    <h4 {...{
      "id": "Collection",
      "style": {
        "position": "relative"
      }
    }}>{`Collection`}</h4>
    <p>{`Each month, companies report data to ONRR on the amount of resources they produced and sold, the value of their sold or used  production, and the amount of royalties they owe the federal government.  Oil and gas companies are required to submit two key monthly reports to ONRR; the first report is the `}<a parentName="p" {...{
        "href": "https://onrr.gov/reporting/production?tabs=forms"
      }}>{`Oil and Gas Operations Report (OGOR)`}</a>{`, specifying the total production and disposition of oil and gas. The second report is for royalties due based on sales or use of the production, the `}<a parentName="p" {...{
        "href": "https://onrr.gov/reporting/revenue?tabs=forms"
      }}>{`Report of Sales and Royalty Remittance, Form ONRR-2014`}</a>{`. Solid minerals companies are only required to submit one monthly `}<a parentName="p" {...{
        "href": "https://onrr.gov/reporting/solids?tabs=forms"
      }}>{`Production and Royalty (P&R) Report, Form ONRR-4430`}</a>{`.`}</p>
    <h4 {...{
      "id": "Validation",
      "style": {
        "position": "relative"
      }
    }}>{`Validation`}</h4>
    <p>{`ONRR is responsible for verifying royalties through its compliance management program, which ensures that the royalty revenues generated from the sales of commodities extracted from leased federal lands are accurately paid and reported. The compliance program includes validation of the reported data using upfront edits, data mining, audits, and compliance reviews.`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Upfront edits: Data goes through initial upfront system edits before being accepted into ONRR’s financial systems.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Data mining: Data mining is a partially automated activity to identify and resolve data errors prior to audits and compliance reviews. Data mining examines large sets of company-reported data for common errors, such as irregularities in the volume of oil or gas extracted.  If errors are identified, ONRR staff work with companies to have them correct the data.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Audits: An audit involves detailed examinations of companies’ royalty payments and corresponding reporting to ONRR.  Audits ensure that royalty payments and other obligations to ONRR comply with lease terms, federal laws and regulations, and other policies.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Compliance reviews: A compliance review is an analysis that determines the reasonableness of company-reported production and royalty data.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "Datasets",
      "style": {
        "position": "relative"
      }
    }}>{`Datasets`}</h3>
    <p>{`The datasets ONRR maintains and provides to the public through this site include production, revenue, and disbursements. These datasets each have their own unique attributes.`}</p>
    <h4 {...{
      "id": "Production-1",
      "style": {
        "position": "relative"
      }
    }}>{`Production`}</h4>
    <p>{`Production data found on this site comes from several places. Oil and gas companies report production on ONRR’s `}<a parentName="p" {...{
        "href": "https://onrr.gov/reporting/production?tabs=forms"
      }}>{`Oil and Gas Operations Report (OGOR)`}</a>{`. Companies report solid minerals production on ONRR’s `}<a parentName="p" {...{
        "href": "https://onrr.gov/reporting/solids?tabs=forms"
      }}>{`Production and Royalty (P&R) Report, Form ONRR-4430`}</a>{`. Companies use these forms to electronically report their production information. The companies' reported data goes through initial upfront system edits before being accepted into ONRR’s financial systems.`}</p>
    <h4 {...{
      "id": "Revenue-2",
      "style": {
        "position": "relative"
      }
    }}>{`Revenue`}</h4>
    <p>{`Revenue data found on this site comes from several places. Oil and gas companies report royalties, rents, and other revenues on `}<a parentName="p" {...{
        "href": "https://onrr.gov/reporting/revenue?tabs=forms"
      }}>{`Form ONRR-2014`}</a>{`. Companies report solid minerals royalties, rents, bonuses, and other revenues on ONRR’s `}<a parentName="p" {...{
        "href": "https://onrr.gov/reporting/solids?tabs=forms"
      }}>{`Production and Royalty (P&R) Report, Form-4430`}</a>{`. Companies use these forms to electronically report their revenue payment information. This company-reported data goes through initial upfront system edits before being accepted into ONRR’s financial systems. Additional revenue comes from ONRR’s accounts receivable data. These payments are not tied to sales volumes and royalties and are submitted directly to ONRR by companies and matched to ONRR-generated receivables. The accounts receivable revenues data includes payments for interest and civil penalties.`}</p>
    <h4 {...{
      "id": "Disbursements-1",
      "style": {
        "position": "relative"
      }
    }}>{`Disbursements`}</h4>
    <p>{`Disbursement data found on this site comes from ONRR.  Once ONRR reconciles the company-reported royalty data and other revenue data with the payments to Treasury, ONRR disburses the funds from the Treasury account to the appropriate federal, state, or tribal accounts. All these transactions are reported and stored in ONRR’s systems.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      